import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';

const DateLogs = () => {
    document.title = "Date Logs";
    
    const [dateData, setDateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        dateType: '',
        status: '',
        dateRange: []
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const [sort, setSort] = useState({
        column: '',
        direction: 'asc' // or 'desc'
    });
    const [dateLogsModal, setDateLogsModal] = useState(false);
    const [selectedDateLogs, setSelectedDateLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [userDetailsModal, setUserDetailsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);

    // Status options for filter
    const statusOptions = [
        'REQUESTED',
        'EXPIRED',
        'COMPLETED',
        'STARTED',
        'ACCEPTED',
        'CANCELLED',
        'JOIN',
        'REJECTED'
    ];

    // Date type options for filter
    const dateTypeOptions = [
        'VIDEO_CALL', 'VOICE_CALL', 'IN_PERSON'
    ];

    // Update the sortData function to handle address sorting
    const sortData = (data, column, direction) => {
        if (!column) return data;

        return [...data].sort((a, b) => {
            // Handle nested properties (e.g., 'requestedByUser.firstName')
            const getValue = (obj, path) => {
                // Special handling for address sorting
                if (path === 'locationOfDateSchedule.address') {
                    const loc = obj.locationOfDateSchedule;
                    if (!loc) return '';
                    return [
                        loc.placeName,
                        loc.address,
                        loc.city,
                        loc.state,
                        loc.zipCode,
                        loc.country
                    ].filter(Boolean).join(' ').toLowerCase();
                }
                return path.split('.').reduce((acc, part) => acc && acc[part], obj);
            };

            let aValue = getValue(a, column);
            let bValue = getValue(b, column);

            // Handle dates
            if (column === 'createdTs' || column === 'scheduleDate') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            // Handle strings
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (direction === 'asc') {
                return aValue < bValue ? -1 : 1;
            } else {
                return aValue > bValue ? -1 : 1;
            }
        });
    };

    // Update the fetchDateLogs function to remove sorting from API call
    const fetchDateLogs = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            let url = `/dates/admin?limit=${pagination.limit}&offset=${offset}`;
            
            // Add other filters but remove sorting
            if (filters.status) {
                url += `&trigger=${filters.status}`;
            }
            if (filters.dateType) {
                url += `&dateType=${filters.dateType}`;
            }
            if (filters.dateRange && filters.dateRange.length === 2) {
                const fromDate = moment(filters.dateRange[0]).format('YYYY-MM-DD');
                const toDate = moment(filters.dateRange[1]).format('YYYY-MM-DD');
                url += `&fromDate=${fromDate}&toDate=${toDate}`;
            }
            
            const response = await APIClient.get(url);
            if (response.data && response.data.data) {
                const { dateData, totalCount } = response.data.data;
                // Sort the data before setting it
                const sortedData = sortData(dateData, sort.column, sort.direction);
                setDateData(sortedData);
                setPagination(prev => ({
                ...prev,
                    currentPage: page,
                    totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching dates:', error);
            toast.error(error.message || 'Failed to fetch date logs', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDateLogs(1); // Reset to first page when filters change
    }, [filters.status, filters.dateType, filters.dateRange, sort]); // Add filters.dateRange and sort as dependencies

    useEffect(() => {
        // Add custom styles for the date picker
        const style = document.createElement('style');
        style.innerHTML = `
            .flatpickr-calendar {
                position: fixed !important;
                top: 50% !important;
                left: 50% !important;
                transform: translate(-50%, -50%) !important;
                z-index: 99999 !important;
            }
            .dropdown-menu {
                max-height: none !important;
                overflow: visible !important;
            }
            body.flatpickr-open {
                overflow: hidden;
            }
            .cursor-pointer {
                cursor: pointer;
            }
            
            .cursor-pointer:hover {
                background-color: rgba(0,0,0,0.05);
            }
            
            th .ri-sort-line {
                opacity: 0.3;
            }
            
            th:hover .ri-sort-line {
                opacity: 1;
            }
            
            .carousel {
                background-color: #f8f9fa;
            }
            
            .carousel-item {
                height: 250px;
            }
            
            .carousel-item img {
                height: 100%;
                object-fit: contain;
                margin: auto;
            }
            
            .modal-body {
                max-height: calc(100vh - 180px);
                overflow-y: auto;
            }
            
            .table th {
                width: 40%;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            dateType: '',
            status: '',
            dateRange: []
        });
    };

    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    const handlePageChange = (page) => {
        fetchDateLogs(page);
    };

    // Update the handleSort function to sort existing data
    const handleSort = (column) => {
        const newDirection = sort.column === column && sort.direction === 'asc' ? 'desc' : 'asc';
        setSort({ column, direction: newDirection });
        // Sort the existing data
        const sortedData = sortData(dateData, column, newDirection);
        setDateData(sortedData);
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">Showing <span className="fw-semibold">{dateData.length}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results</div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && handlePageChange(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    const fetchDateActionLogs = async (dateId) => {
        try {
            setLoadingLogs(true);
            const response = await APIClient.get(`/datesActionLogs/admin?limit=10&offset=0&dateId=${dateId}`);
            if (response.data && response.data.data) {
                setSelectedDateLogs(response.data.data.dateData);
                setDateLogsModal(true);
            }
        } catch (error) {
            console.error('Error fetching date logs:', error);
            toast.error(error.message || 'Failed to fetch date action logs', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
        } finally {
            setLoadingLogs(false);
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            setLoadingUser(true);
            const authUser = JSON.parse(sessionStorage.getItem("authUser"));
            const contactMasked = authUser?.userContactMasked ? 1 : 0;
            
            const response = await APIClient.get(`/python/users/?userId=${userId}&contactMasked=${contactMasked}`);
            if (response.data && response.data.result && response.data.result.length > 0) {
                setSelectedUser(response.data.result[0]);
                setUserDetailsModal(true);
            }
        } catch (error) {
            toast.error("Failed to fetch user details");
        } finally {
            setLoadingUser(false);
        }
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Container fluid>
                <BreadCrumb title="Date Logs" pageTitle="Logs" />
                                                <Row>
                                                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center">
                                <h4 className="card-title mb-0 flex-grow-1">Date Logs</h4>
                                                            <div className="d-flex gap-2">
                                    {/* Filters */}
                                    <UncontrolledDropdown>
                                        <DropdownToggle tag="button" className="btn btn-soft-info">
                                            <i className="ri-filter-3-line align-bottom"></i> Filter
                                        </DropdownToggle>
                                        <DropdownMenu className="p-3" style={{ minWidth: "300px", maxHeight: "none" }}>
                                                        <div className="mb-3">
                                                <Label>Date Type</Label>
                                                                <Input 
                                                    type="select" 
                                                    value={filters.dateType}
                                                    onChange={(e) => handleFilterChange('dateType', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    {dateTypeOptions.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </Input>
                                                            </div>
                                                        <div className="mb-3">
                                                <Label>Status</Label>
                                                                <Input 
                                                    type="select"
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    {statusOptions.map(status => (
                                                        <option key={status} value={status}>{status}</option>
                                                    ))}
                                                </Input>
                                                            </div>
                                                        <div className="mb-3">
                                                <Label>Date Range</Label>
                                                <Flatpickr
                                                                    className="form-control"
                                                    options={{
                                                        mode: "range",
                                                        dateFormat: "Y-m-d",
                                                        enableTime: false,
                                                        static: true,
                                                        onChange: (dates) => {
                                                            handleFilterChange('dateRange', dates);
                                                            // Only close if both dates are selected
                                                            if (dates.length === 2) {
                                                                document.querySelector('.flatpickr-calendar').style.display = 'none';
                                                            }
                                                        }
                                                    }}
                                                    value={filters.dateRange}
                                                                />
                                                            </div>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                                                        <Button 
                                        color="warning" 
                                        onClick={clearFilters}
                                        disabled={!filters.dateType && !filters.status && !filters.dateRange.length}
                                    >
                                        <i className="ri-refresh-line align-bottom"></i> Clear Filters
                                                                        </Button>
                                                                    </div>
                                                    </CardHeader>
                                                    <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                {[
                                                    { id: 'slNo', label: 'SL No', sortKey: null },
                                                    { id: 'createdTs', label: 'Created On', sortKey: 'createdTs' },
                                                    { id: 'createdBy', label: 'Created By', sortKey: 'requestedByUser.firstName' },
                                                    { id: 'requestedUser', label: 'Requested User', sortKey: 'requestedToUser.firstName' },
                                                    { id: 'dateType', label: 'Date Type', sortKey: 'dateType' },
                                                    { id: 'dateAddress', label: 'Date Address', sortKey: 'locationOfDateSchedule.address' },
                                                    { id: 'scheduleDate', label: 'Scheduled For', sortKey: 'scheduleDate' },
                                                    { id: 'creatorRating', label: 'Creator Rating', sortKey: 'creatorRating' },
                                                    { id: 'requestedUserRating', label: 'Requested User Rating', sortKey: 'requestedUserRating' },
                                                    { id: 'status', label: 'Status', sortKey: 'status' },
                                                    { id: 'action', label: 'Action', sortKey: null }
                                                ].map(column => (
                                                    <th 
                                                        key={column.id} 
                                                        scope="col" 
                                                        className={column.sortKey ? 'cursor-pointer' : ''}
                                                        onClick={() => column.sortKey && handleSort(column.sortKey)}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            {column.label}
                                                            {column.sortKey && (
                                                                <span className="ms-2">
                                                                    {sort.column === column.sortKey ? (
                                                                        <i className={`ri-sort-${sort.direction === 'asc' ? 'asc' : 'desc'}`}></i>
                                                                    ) : (
                                                                        <i className="ri-sort-line text-muted"></i>
                                                                    )}
                                                                            </span>
                                                            )}
                                                                    </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dateData.map((date, index) => (
                                                <tr key={date._id}>
                                                    <td>{index + 1}</td>
                                                    <td>{moment(date.createdTs).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                                                <img 
                                                                    src={getImageUrl(date.requestedByUser.profilePic[0]?.url)} 
                                                                    alt={`${date.requestedByUser.firstName}'s avatar`}
                                                                    className="rounded w-100 h-100"
                                                                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                                                                    onClick={() => fetchUserDetails(date.requestedByUser._id)}
                                                                    />
                                                                </div>
                                                            <div className="flex-grow-1">
                                                                <Link to="#" onClick={() => fetchUserDetails(date.requestedByUser._id)}>
                                                                    {date.requestedByUser.firstName} {date.requestedByUser.lastName}
                                                                </Link>
                                                                    </div>
                                                                </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                                                <img 
                                                                    src={getImageUrl(date.requestedToUser.profilePic[0]?.url)} 
                                                                    alt={`${date.requestedToUser.firstName}'s avatar`}
                                                                    className="rounded w-100 h-100"
                                                                    style={{ objectFit: 'cover', cursor: 'pointer' }}
                                                                    onClick={() => fetchUserDetails(date.requestedToUser._id)}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = "/images/default-avatar.png";
                                                                    }}
                                                                    />
                                                                </div>
                                                            <div className="flex-grow-1">
                                                                <Link to="#" onClick={() => fetchUserDetails(date.requestedToUser._id)}>
                                                                    {date.requestedToUser.firstName} {date.requestedToUser.lastName}
                                                                </Link>
                                                                    </div>
                                                                </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${
                                                            date?.dateType === 'VIDEO_CALL' ? 'info' : 
                                                            date?.dateType === 'VOICE_CALL' ? 'warning' : 'success'
                                                        } text-dark`}>
                                                            <i className={`ri-${
                                                                date?.dateType === 'VIDEO_CALL' ? 'video-line' : 
                                                                date?.dateType === 'VOICE_CALL' ? 'phone-line' : 'map-pin-line'
                                                            } align-bottom me-1`}></i>
                                                            {String(date?.dateType).replace(/_/g, ' ')}
                                                                            </span>
                                                    </td>
                                                    <td>
                                                        {date.dateType === 'IN_PERSON' && date.locationOfDateSchedule ? (
                                                            <div 
                                                                style={{ 
                                                                    maxWidth: '200px',
                                                                    lineHeight: '1.2'
                                                                }}
                                                                title={`${date.locationOfDateSchedule.placeName || ''} ${date.locationOfDateSchedule.address || ''} ${date.locationOfDateSchedule.city || ''} ${date.locationOfDateSchedule.state || ''} ${date.locationOfDateSchedule.zipCode || ''} ${date.locationOfDateSchedule.country || ''}`}
                                                            >
                                                                <div style={{ 
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {date.locationOfDateSchedule.placeName}, {date.locationOfDateSchedule.address}
                                                                    </div>
                                                                <div style={{ 
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {date.locationOfDateSchedule.city}, {date.locationOfDateSchedule.state}, {date.locationOfDateSchedule.zipCode}, {date.locationOfDateSchedule.country}
                                                                </div>
                                                        </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td>{moment(date.scheduleDate).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <i className="ri-star-fill text-warning me-1"></i>
                                                        {/* Add actual rating when available */}
                                                        N/A
                                                    </td>
                                                    <td>
                                                        <i className="ri-star-fill text-warning me-1"></i>
                                                        {/* Add actual rating when available */}
                                                        N/A
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${
                                                            date?.status === 'COMPLETED' ? 'success' :
                                                            date?.status === 'CANCELLED' || date?.status === 'REJECTED' ? 'danger' :
                                                            date?.status === 'ACCEPTED' ? 'info' :
                                                            date?.status === 'REQUESTED' ? 'primary' :
                                                            date?.status === 'JOIN' ? 'warning' : 'secondary'
                                                        } text-dark`}>
                                                            {String(date?.status)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            color="info" 
                                                            size="sm"
                                                            className="btn-soft-info"
                                                            onClick={() => {
                                                                setSelectedDate(date);
                                                                fetchDateActionLogs(date._id);
                                                            }}
                                                        >
                                                            <i className="ri-file-list-3-line align-bottom"></i> View Logs
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {renderPagination()}
                                                    </CardBody>
                                                </Card>
                                                            </Col>
                                                        </Row>
            </Container>
            {/* Add loading overlay */}
            {loading && (
                <div className="overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <Spinner color="success"> Loading... </Spinner>
                </div>
            )}
            <Modal
                isOpen={dateLogsModal}
                toggle={() => setDateLogsModal(!dateLogsModal)}
                size="xl"
            >
                <ModalHeader toggle={() => setDateLogsModal(!dateLogsModal)}>
                    Date Logs
                </ModalHeader>
                <ModalBody>
                    {loadingLogs ? (
                        <div className="text-center">
                            <Spinner color="success"> Loading... </Spinner>
                                                                    </div>
                    ) : (
                        <div className="table-responsive">
                            <Table className="table-striped table-nowrap align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>SL No</th>
                                        <th>Date Action</th>
                                        <th>By User</th>
                                        <th>Logs Time</th>
                                        <th>Date Time</th>
                                        <th>Auto End Time</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedDateLogs.map((log, index) => (
                                        <tr key={log._id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className={`badge bg-${
                                                    log.action === 'CANCELLED' ? 'danger' :
                                                    log.action === 'REQUESTED' ? 'primary' :
                                                    log.action === 'ACCEPTED' ? 'success' :
                                                    'secondary'
                                                } text-dark`}>
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td style={{cursor: 'pointer'}} onClick={() => fetchUserDetails(log.user._id)}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                                        <img 
                                                            src={getImageUrl(log.user.profilePic[0]?.url)}
                                                            alt={`${log.user.firstName}'s avatar`}
                                                            className="rounded w-100 h-100"
                                                            style={{ objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "/images/default-avatar.png";
                                                            }}
                                                        />
                                                                    </div>
                                                    <div className="flex-grow-1">
                                                        {log.user.firstName} {log.user.lastName}
                                                                </div>
                                                                    </div>
                                            </td>
                                            <td>{moment(log.createdTs).format('DD MMM YYYY, hh:mm A')}</td>
                                            <td>{moment(log.scheduleDate).format('DD MMM YYYY, hh:mm A')}</td>
                                            <td>
                                                {selectedDate?.endTs ? 
                                                    moment(selectedDate.endTs).format('DD MMM YYYY, hh:mm A') : 
                                                    '-'
                                                }
                                            </td>
                                            <td>-</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                                                                </div>
                    )}
                </ModalBody>
            </Modal>
            <UserDetailsModal 
                isOpen={userDetailsModal}
                toggle={() => setUserDetailsModal(false)}
                user={selectedUser}
                loading={loadingUser}
            />
        </div>
    );
};

export default DateLogs;